import { useAdminDashboardStats } from '../../hooks/use-admin';
import {
  Users,
  FileText,
  MessageSquareWarning,
  Shield,
  AlertTriangle,
  TrendingUp,
  UserX,
  EyeOff,
  Image,
  Video,
  Flag,
  AlertOctagon,
  Camera,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

function StatCard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  variant = 'default',
}) {
  const variants = {
    default: 'bg-zinc-900/50 border-zinc-800',
    warning: 'bg-yellow-500/10 border-yellow-500/20',
    danger: 'bg-red-500/10 border-red-500/20',
    success: 'bg-green-500/10 border-green-500/20',
  };

  const iconVariants = {
    default: 'text-blue-400 bg-blue-500/10',
    warning: 'text-yellow-400 bg-yellow-500/10',
    danger: 'text-red-400 bg-red-500/10',
    success: 'text-green-400 bg-green-500/10',
  };

  return (
    <Card className={cn('border', variants[variant])}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-zinc-400">{title}</p>
            <p className="text-3xl font-bold text-white mt-1">{value}</p>
            {description && (
              <p className="text-xs text-zinc-500 mt-1">{description}</p>
            )}
          </div>
          <div className={cn('p-3 rounded-lg', iconVariants[variant])}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        {trend && (
          <div className="mt-4 flex items-center gap-1 text-xs">
            <TrendingUp className="h-3 w-3 text-green-400" />
            <span className="text-green-400">{trend}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { data, isLoading, error } = useAdminDashboardStats();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-zinc-400">Overview of platform activity</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="border border-zinc-800 bg-zinc-900/50">
              <CardContent className="p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-zinc-800 rounded w-1/2"></div>
                  <div className="h-8 bg-zinc-800 rounded w-1/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            Failed to load dashboard
          </h2>
          <p className="text-zinc-400">{error.message}</p>
        </div>
      </div>
    );
  }

  const { stats } = data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-zinc-400">
          Overview of platform activity and moderation status
        </p>
      </div>

      {/* User Stats */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-400" />
          Users
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={stats?.users?.total || 0}
            icon={Users}
          />
          <StatCard
            title="Active Users"
            value={stats?.users?.active || 0}
            icon={Users}
            variant="success"
          />
          <StatCard
            title="Suspended"
            value={stats?.users?.suspended || 0}
            icon={UserX}
            variant={stats?.users?.suspended > 0 ? 'warning' : 'default'}
          />
          <StatCard
            title="Banned"
            value={stats?.users?.banned || 0}
            icon={UserX}
            variant={stats?.users?.banned > 0 ? 'danger' : 'default'}
          />
        </div>
      </div>

      {/* Content Stats */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-400" />
          Content
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Discussions"
            value={stats?.content?.totalDiscussions || 0}
            icon={FileText}
          />
          <StatCard
            title="Active Discussions"
            value={stats?.content?.activeDiscussions || 0}
            icon={FileText}
            variant="success"
          />
          <StatCard
            title="Hidden Content"
            value={stats?.content?.hiddenDiscussions || 0}
            icon={EyeOff}
            variant={
              stats?.content?.hiddenDiscussions > 0 ? 'warning' : 'default'
            }
          />
          <StatCard
            title="Media Files"
            value={stats?.media?.total || 0}
            icon={Image}
            description={`${stats?.media?.images || 0} images, ${
              stats?.media?.videos || 0
            } videos`}
          />
        </div>
      </div>

      {/* Moderation Stats */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-400" />
          Moderation
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Pending Reports"
            value={stats?.reports?.pending || 0}
            icon={MessageSquareWarning}
            variant={
              (stats?.reports?.pending || 0) > 5
                ? 'danger'
                : (stats?.reports?.pending || 0) > 0
                ? 'warning'
                : 'default'
            }
            description={
              (stats?.reports?.pending || 0) > 0
                ? 'Requires attention'
                : 'All clear'
            }
          />
          <StatCard
            title="Reports Today"
            value={stats?.reports?.today || 0}
            icon={MessageSquareWarning}
          />
          <StatCard
            title="Critical Reports"
            value={stats?.reports?.critical || 0}
            icon={AlertTriangle}
            variant={(stats?.reports?.critical || 0) > 0 ? 'danger' : 'default'}
            description="High priority items"
          />
          <StatCard
            title="Actions Today"
            value={stats?.moderation?.actionsToday || 0}
            icon={Shield}
            description="Moderation actions taken"
          />
        </div>
      </div>

      {/* Reports Breakdown */}
      {(stats?.reports?.byType || stats?.reports?.byReason) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Reports by Type */}
          <Card className="border border-zinc-800 bg-zinc-900/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Flag className="h-5 w-5 text-yellow-400" />
                Pending Reports by Type
              </CardTitle>
              <CardDescription>
                Distribution of reported content types
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {stats?.reports?.byType &&
              Object.keys(stats.reports.byType).length > 0 ? (
                Object.entries(stats.reports.byType).map(([type, count]) => {
                  const total = Object.values(stats.reports.byType).reduce(
                    (sum, c) => sum + c,
                    0
                  );
                  const percentage =
                    total > 0 ? Math.round((count / total) * 100) : 0;
                  const icons = {
                    discussion: FileText,
                    comment: MessageSquareWarning,
                    user: Users,
                    image: Image,
                    video: Video,
                    media: Camera,
                  };
                  const Icon = icons[type] || Flag;
                  return (
                    <div key={type} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-zinc-400" />
                          <span className="text-sm text-zinc-300 capitalize">
                            {type}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-white">
                          {count}
                        </span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  );
                })
              ) : (
                <p className="text-zinc-500 text-sm text-center py-4">
                  No pending reports
                </p>
              )}
            </CardContent>
          </Card>

          {/* Reports by Reason */}
          <Card className="border border-zinc-800 bg-zinc-900/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertOctagon className="h-5 w-5 text-red-400" />
                Pending Reports by Reason
              </CardTitle>
              <CardDescription>Why content is being reported</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {stats?.reports?.byReason &&
              Object.keys(stats.reports.byReason).length > 0 ? (
                Object.entries(stats.reports.byReason)
                  .slice(0, 8)
                  .map(([reason, count]) => {
                    const reasonColors = {
                      spam: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                      harassment:
                        'bg-orange-500/20 text-orange-400 border-orange-500/30',
                      hate_speech:
                        'bg-red-500/20 text-red-400 border-red-500/30',
                      misinformation:
                        'bg-blue-500/20 text-blue-400 border-blue-500/30',
                      inappropriate_content:
                        'bg-purple-500/20 text-purple-400 border-purple-500/30',
                      pornographic:
                        'bg-pink-500/20 text-pink-400 border-pink-500/30',
                      violent: 'bg-red-500/20 text-red-400 border-red-500/30',
                      fraudulent:
                        'bg-purple-500/20 text-purple-400 border-purple-500/30',
                      stolen_content:
                        'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
                      illegal_content:
                        'bg-red-500/20 text-red-400 border-red-500/30',
                      copyright_violation:
                        'bg-blue-500/20 text-blue-400 border-blue-500/30',
                      other: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
                    };
                    const colorClass =
                      reasonColors[reason] || reasonColors.other;
                    return (
                      <div
                        key={reason}
                        className="flex justify-between items-center"
                      >
                        <Badge
                          variant="outline"
                          className={cn('capitalize', colorClass)}
                        >
                          {reason?.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-sm font-medium text-white">
                          {count}
                        </span>
                      </div>
                    );
                  })
              ) : (
                <p className="text-zinc-500 text-sm text-center py-4">
                  No pending reports
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Critical Reports Alert */}
      {(stats?.reports?.critical || 0) > 0 && (
        <Card className="border border-red-500/30 bg-red-500/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-white">
                  Critical Reports Pending
                </h4>
                <p className="text-sm text-red-300 mt-1">
                  There are {stats.reports.critical} high-priority reports that
                  require immediate attention.
                </p>
              </div>
              <a href="/admin/reports?priority=critical">
                <Button variant="destructive" size="sm">
                  Review Now
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors cursor-pointer">
            <a href="/admin/reports?status=pending">
              <CardContent className="p-4 flex items-center gap-3">
                <MessageSquareWarning className="h-5 w-5 text-yellow-400" />
                <span className="text-white">Review Pending Reports</span>
              </CardContent>
            </a>
          </Card>
          <Card className="border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors cursor-pointer">
            <a href="/admin/content?tab=media">
              <CardContent className="p-4 flex items-center gap-3">
                <Image className="h-5 w-5 text-purple-400" />
                <span className="text-white">Moderate Media</span>
              </CardContent>
            </a>
          </Card>
          <Card className="border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors cursor-pointer">
            <a href="/admin/users?isSuspended=true">
              <CardContent className="p-4 flex items-center gap-3">
                <UserX className="h-5 w-5 text-orange-400" />
                <span className="text-white">View Suspended Users</span>
              </CardContent>
            </a>
          </Card>
          <Card className="border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/50 transition-colors cursor-pointer">
            <a href="/admin/audit-logs">
              <CardContent className="p-4 flex items-center gap-3">
                <Shield className="h-5 w-5 text-green-400" />
                <span className="text-white">View Audit Logs</span>
              </CardContent>
            </a>
          </Card>
        </div>
      </div>
    </div>
  );
}
